import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function Datenschutz() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://physio-muehlbacher.at';

    document.title = 'Datenschutzerklärung – Physiotherapie Theresa Mühlbacher | Munderfing';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Datenschutzerklärung der Physiotherapiepraxis Theresa Mühlbacher in Munderfing (OÖ). Informationen zu Datenverarbeitung, DSGVO-Rechten und Kontaktformular gemäß österreichischem Recht.'
      );
    }

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/datenschutz`,
      url: `${siteUrl}/datenschutz`,
      name: 'Datenschutzerklärung – Physiotherapie Theresa Mühlbacher',
      description:
        'DSGVO-konforme Datenschutzerklärung der Physiotherapiepraxis Theresa Mühlbacher in Munderfing, Österreich.',
      inLanguage: 'de-AT',
      isPartOf: {
        '@id': `${siteUrl}/#website`,
      },
      publisher: {
        '@type': 'Person',
        name: 'Theresa Mühlbacher',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Achenlohe 13',
          addressLocality: 'Munderfing',
          postalCode: '5222',
          addressCountry: 'AT',
        },
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Startseite',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Datenschutzerklärung',
          item: `${siteUrl}/datenschutz`,
        },
      ],
    };

    const schemas = [webPageSchema, breadcrumbSchema];
    const scriptTags: HTMLScriptElement[] = schemas.map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = `schema-datenschutz-${i}`;
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      scriptTags.forEach((el) => {
        if (document.head.contains(el)) document.head.removeChild(el);
      });
      document.title =
        'Physiotherapie Munderfing – Theresa Mühlbacher | Rücken, Gelenk & Beckenbodentherapie';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="bg-olive-900 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gold-300 text-sm font-medium mb-8 hover:text-gold-400 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Zurück zur Startseite
          </Link>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-white mb-4">
            Datenschutzerklärung
          </h1>
          <div className="w-16 h-px bg-gold-300"></div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">

        {/* Einleitung */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            1. Datenschutz auf einen Blick
          </h2>
          <div className="space-y-4 text-olive-700 leading-relaxed">
            <p>
              Der Schutz deiner persönlichen Daten ist mir ein besonderes Anliegen. Ich verarbeite deine Daten daher ausschließlich auf Grundlage der gesetzlichen Bestimmungen (DSGVO, TKG 2003). In dieser Datenschutzerklärung informiere ich dich über die wichtigsten Aspekte der Datenverarbeitung im Rahmen meiner Website.
            </p>
          </div>
        </section>

        {/* Verantwortliche */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            2. Verantwortliche Person
          </h2>
          <div className="space-y-3 text-olive-700 leading-relaxed">
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Name:</span>
              <span className="sm:col-span-2">Theresa Mühlbacher</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Adresse:</span>
              <span className="sm:col-span-2">Achenlohe 13, 5222 Munderfing, Österreich</span>
            </div>
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">Telefon:</span>
              <span className="sm:col-span-2">
                <a href="tel:+436603450403" className="hover:text-gold-600 transition-colors">
                  +43 660 3450403
                </a>
              </span>
            </div>
            <div className="grid sm:grid-cols-3 gap-1">
              <span className="font-medium text-olive-900">E-Mail:</span>
              <span className="sm:col-span-2">
                <a href="mailto:Theresa.weiss1991@gmail.com" className="hover:text-gold-600 transition-colors">
                  Theresa.weiss1991@gmail.com
                </a>
              </span>
            </div>
          </div>
        </section>

        {/* Hosting & Server */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            3. Hosting
          </h2>
          <p className="text-olive-700 leading-relaxed">
            Diese Website wird bei einem externen Dienstleister (Hoster) betrieben. Beim Aufrufen dieser Website werden durch den Hoster automatisch Server-Log-Dateien gespeichert (z. B. IP-Adresse, Browsertyp, Datum und Uhrzeit des Aufrufs). Diese Daten werden nicht mit anderen Datenquellen zusammengeführt und dienen ausschließlich der technischen Bereitstellung der Website.
          </p>
        </section>

        {/* Kontaktformular */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            4. Kontaktformular
          </h2>
          <div className="space-y-4 text-olive-700 leading-relaxed">
            <p>
              Wenn du mir über das Kontaktformular eine Nachricht zukommen lässt, werden deine angegebenen Daten (Name, E-Mail-Adresse, Telefonnummer und Nachricht) zum Zweck der Bearbeitung deiner Anfrage verarbeitet. Diese Daten werden nicht ohne deine Einwilligung weitergegeben.
            </p>
            <p>
              <strong className="text-olive-900">Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung oder vorvertragliche Maßnahmen) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an der Beantwortung von Anfragen).
            </p>
            <p>
              Die übermittelten Daten werden nach abschließender Bearbeitung deiner Anfrage gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.
            </p>
          </div>
        </section>

        {/* Terminbuchung Calendly */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            5. Online-Terminbuchung (Calendly)
          </h2>
          <div className="space-y-4 text-olive-700 leading-relaxed">
            <p>
              Für die Online-Terminbuchung nutze ich den Dienst <strong className="text-olive-900">Calendly</strong> (Calendly LLC, 271 17th St NW, Atlanta, GA 30363, USA). Beim Buchen eines Termins werden deine eingegebenen Daten (Name, E-Mail-Adresse, gewünschter Termin) an Calendly übermittelt und dort verarbeitet.
            </p>
            <p>
              Calendly ist unter dem EU-US Data Privacy Framework zertifiziert. Weitere Informationen findest du in der{' '}
              <a
                href="https://calendly.com/privacy"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-gold-600 hover:underline"
              >
                Datenschutzerklärung von Calendly
              </a>.
            </p>
            <p>
              <strong className="text-olive-900">Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. b DSGVO.
            </p>
          </div>
        </section>

        {/* Cookies */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            6. Cookies
          </h2>
          <div className="space-y-4 text-olive-700 leading-relaxed">
            <p>
              Diese Website verwendet technisch notwendige Cookies, die für den Betrieb der Seite erforderlich sind. Es werden keine Tracking- oder Werbe-Cookies eingesetzt. Technisch notwendige Cookies können nicht deaktiviert werden, da ohne sie die Website nicht ordnungsgemäß funktioniert.
            </p>
          </div>
        </section>

        {/* Deine Rechte */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            7. Deine Rechte
          </h2>
          <div className="space-y-4 text-olive-700 leading-relaxed">
            <p>Du hast gegenüber mir folgende Rechte hinsichtlich deiner personenbezogenen Daten:</p>
            <ul className="space-y-3 pl-4">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-gold-500 text-lg flex-shrink-0 mt-0.5"></i>
                <span><strong className="text-olive-900">Auskunftsrecht</strong> (Art. 15 DSGVO): Du kannst jederzeit Auskunft über die zu deiner Person gespeicherten Daten verlangen.</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-gold-500 text-lg flex-shrink-0 mt-0.5"></i>
                <span><strong className="text-olive-900">Berichtigungsrecht</strong> (Art. 16 DSGVO): Du kannst die Berichtigung unrichtiger Daten verlangen.</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-gold-500 text-lg flex-shrink-0 mt-0.5"></i>
                <span><strong className="text-olive-900">Löschungsrecht</strong> (Art. 17 DSGVO): Du kannst die Löschung deiner Daten verlangen, soweit keine gesetzliche Aufbewahrungspflicht besteht.</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-gold-500 text-lg flex-shrink-0 mt-0.5"></i>
                <span><strong className="text-olive-900">Einschränkungsrecht</strong> (Art. 18 DSGVO): Du kannst die Einschränkung der Verarbeitung deiner Daten verlangen.</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-gold-500 text-lg flex-shrink-0 mt-0.5"></i>
                <span><strong className="text-olive-900">Widerspruchsrecht</strong> (Art. 21 DSGVO): Du kannst der Verarbeitung deiner Daten widersprechen.</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-gold-500 text-lg flex-shrink-0 mt-0.5"></i>
                <span><strong className="text-olive-900">Beschwerderecht</strong>: Du hast das Recht, eine Beschwerde bei der österreichischen Datenschutzbehörde (dsb.gv.at) einzureichen.</span>
              </li>
            </ul>
            <p>
              Zur Ausübung deiner Rechte wende dich bitte per E-Mail an:{' '}
              <a href="mailto:Theresa.weiss1991@gmail.com" className="text-gold-600 hover:underline">
                Theresa.weiss1991@gmail.com
              </a>
            </p>
          </div>
        </section>

        {/* Aktualität */}
        <section className="mb-16">
          <h2 className="font-serif text-2xl font-semibold text-olive-900 mb-6 pb-3 border-b border-olive-100">
            8. Aktualität dieser Datenschutzerklärung
          </h2>
          <p className="text-olive-700 leading-relaxed">
            Diese Datenschutzerklärung hat den Stand März 2026. Ich behalte mir vor, diese Datenschutzerklärung anzupassen, damit sie stets den aktuellen rechtlichen Anforderungen entspricht oder um Änderungen meiner Leistungen in der Datenschutzerklärung umzusetzen.
          </p>
        </section>

        <div className="border-t border-olive-100 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-olive-900 text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-olive-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Zurück zur Startseite
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-olive-900 text-white/60 text-sm py-6 px-6 mt-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
          <p>© 2025 Physiotherapie Theresa Mühlbacher. Alle Rechte vorbehalten.</p>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
              Impressum
            </Link>
            <Link to="/datenschutz" className="hover:text-gold-300 transition-colors cursor-pointer whitespace-nowrap">
              Datenschutzerklärung
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
